import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useGetContactDuplicatesItemQuery, useUpdateContactDuplicatesItemMutation } from '../../api/contact.js';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import BaseInput from '../../components/form/BaseInput.jsx';
import BaseRadio from '../../components/form/BaseRadio.jsx';
import BaseCheckboxThemeGeneral from '../../components/form/BaseCheckboxThemeGeneral.jsx';
import { useLocation, useNavigate } from 'react-router-dom';
import ContactCompaniesCarouselDuplicates from '../../components/misc/ContactCompaniesCarouselDuplicates.jsx';
import NeedsAndSkillsDuplicates from '../../components/misc/NeedsAndSkillsDuplicates.jsx';
import SharedModelListItem from '../../components/misc/SharedModelListItem.jsx';
import PrivateUsersListItem from '../../components/misc/PrivateUsersListItem.jsx';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { useSelector } from 'react-redux';
import { PERMISSION_CONTACTS } from '../../utils/constants.js';
import BaseSwitch from '../../components/form/BaseSwitch.jsx';
import BaseButton from '../../components/form/BaseButton.jsx';
import IconClose from '../../components/images/IconClose.jsx';
import { useGetPoliticalAffiliationsQuery } from '../../api/misc.js';
import BaseDropdown from '../../components/form/BaseDropdown.jsx';
import BaseRating from '../../components/form/BaseRating.jsx';

function useQuery() {
    return new URLSearchParams(useLocation().search);
}

const singleFields = [
    'first_name',
    'middle_name',
    'last_name',
    'email',
    'phone',
    'reputation',
    'political_affiliation'
];
const objectFields = ['country_id'];
const multiFields = ['social_links'];

const ContactDuplicatesDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const query = useQuery();
    const duplicateId = location.pathname.split('/').pop();
    const contactIds = query.get('contactIds');
    const user = useSelector((state) => state.user.user);

    const { data, isLoading, error } = useGetContactDuplicatesItemQuery(
        { id: duplicateId, contactIds },
        { skip: !(duplicateId && contactIds) }
    );

    const [updateContactDuplicatesItem, { isLoading: isUpdating, error: updateError }] =
        useUpdateContactDuplicatesItemMutation();

    const { data: politicalAffiliationsData } = useGetPoliticalAffiliationsQuery();
    const politicalOptions =
        politicalAffiliationsData?.response?.political_affiliations?.map((pa) => ({
            label: pa.label,
            value: pa.value
        })) || [];

    const { control, handleSubmit, reset, watch, setValue, clearErrors } = useForm({
        defaultValues: {
            mergeData: {},
            selected: {},
            mergeMulti: {},
            needs: [],
            skills: [],
            shared: false,
            shared_organizations: [],
            shared_users: [],
            news_enabled: false,
            news_frequency: 'daily',
            news_keywords: []
        }
    });

    const { fields: noteFields } = useFieldArray({
        control,
        name: 'mergeMulti.notes'
    });

    const [formReady, setFormReady] = useState(false);
    const [isPrivate, setIsPrivate] = useState(true);
    const [permittedOrganizations, setPermittedOrganizations] = useState([]);
    const [newsKeyword, setNewsKeyword] = useState('');

    const [initialMergeData, setInitialMergeData] = useState({});
    const [initialMergeMulti, setInitialMergeMulti] = useState({});

    useEffect(() => {
        if (!user?.role?.permissions || !user?.organizations) return;
        user.organizations.forEach((org) => {
            const perm = user.role.permissions.find(
                (p) => p.organization_id === org.id && p.permission === PERMISSION_CONTACTS
            );
            if (perm && (perm.create || perm.update)) {
                setPermittedOrganizations((prev) => (prev.some((o) => o.id === org.id) ? prev : [...prev, org]));
            }
        });
    }, [user]);

    useEffect(() => {
        if (data && data.response && data.response.duplicate_data) {
            const dupData = data.response.duplicate_data;
            const mergeData = {};
            const selected = {};
            singleFields.forEach((field) => {
                if (dupData[field] && Array.isArray(dupData[field]) && dupData[field].length > 0) {
                    mergeData[field] = dupData[field].map((item) => ({ value: item }));
                    selected[field] = 0;
                }
            });

            objectFields.forEach((field) => {
                if (dupData[field] && typeof dupData[field] === 'object' && Object.keys(dupData[field]).length > 0) {
                    mergeData[field] = Object.values(dupData[field]).map((item) => ({ value: item.country_name }));
                    selected[field] = 0;
                }
            });

            const mergeMulti = {};
            multiFields.forEach((field) => {
                if (dupData[field] && Array.isArray(dupData[field]) && dupData[field].length > 0) {
                    mergeMulti[field] = dupData[field].map((item) => {
                        if (item && typeof item === 'object') {
                            return {
                                type: item.type,
                                value: item.url,
                                keep: true
                            };
                        } else {
                            return {
                                type: null,
                                value: item,
                                keep: true
                            };
                        }
                    });
                }
            });

            if (dupData.notes?.length > 0) {
                mergeMulti.notes = dupData.notes.map((item) => ({
                    value: item,
                    keep: true
                }));
            }

            reset({
                mergeData,
                selected,
                mergeMulti,
                companies: dupData.companies,
                needs: dupData.needs ? dupData.needs.map((item) => ({ name: item.name, need_id: item.need_id })) : [],
                skills: dupData.skills
                    ? dupData.skills.map((item) => ({ name: item.name, skill_id: item.skill_id }))
                    : [],
                shared: dupData.shared,
                shared_organizations: dupData.shared_organizations || [],
                shared_users: dupData.shared_users || [],
                news_enabled: dupData.news_enabled ?? false,
                news_frequency: dupData.news_frequency ?? 'daily',
                news_keywords: dupData.news_keywords ?? [],
                ...(dupData.associated_user_id ? { associated_user_id: dupData.associated_user_id } : {})
            });
            setInitialMergeData(mergeData);
            setInitialMergeMulti(mergeMulti);
            setIsPrivate(!dupData.shared);
            setFormReady(true);
        }
    }, [data, reset]);

    const mergeDataValues = initialMergeData;
    const mergeMultiValues = initialMergeMulti;

    const sharedOrganizations = watch('shared_organizations') || [];
    const sharedUsers = watch('shared_users') || [];
    const newsEnabled = watch('news_enabled');
    const newsKeywords = watch('news_keywords') || [];

    const handleSharedOrganizations = (idx, value) => {
        const orgId = permittedOrganizations[idx].id;
        const next = value ? [...sharedOrganizations, orgId] : sharedOrganizations.filter((o) => o !== orgId);
        setValue('shared_organizations', next);
        if (next.length === 0) handleSharedUsers(true);
    };

    const handleSharedUsers = (value) => {
        setIsPrivate(value);
        if (value) {
            setValue('shared_organizations', []);
        } else {
            setValue('shared_users', []);
            if (permittedOrganizations.length) {
                handleSharedOrganizations(0, true);
            }
        }
    };

    const handleUserMark = (id, value) => {
        const next = value ? [...sharedUsers, id] : sharedUsers.filter((u) => u !== id);
        setValue('shared_users', next);
    };

    const renderShared = () => {
        if (!permittedOrganizations.length) {
            return <div className='text-theme1-text1 text-lg'>No organizations found.</div>;
        }
        return (
            <div>
                {permittedOrganizations.map(({ id, name }, i) => (
                    <SharedModelListItem
                        key={id}
                        index={i}
                        text={name}
                        isMarked={sharedOrganizations.includes(id)}
                        onChangeSwitch={handleSharedOrganizations}
                        isLoading={isLoading}
                        disabled={isPrivate}
                    />
                ))}
                <PrivateUsersListItem
                    key='private'
                    text='Private'
                    isMarked={isPrivate}
                    onChangeSwitch={handleSharedUsers}
                    onChangeMark={handleUserMark}
                    modelSharedUsers={sharedUsers}
                    isLoading={isLoading}
                    permission={PERMISSION_CONTACTS}
                />
            </div>
        );
    };

    const handleAddNewsKeyword = () => {
        const trimmed = newsKeyword.trim();
        if (!trimmed) return;
        setValue('news_keywords', [...newsKeywords, trimmed]);
        setNewsKeyword('');
    };

    const onSubmit = async (formData) => {
        const pickSingle = (field) => {
            const idx = formData.selected?.[field] ?? 0;
            return formData.mergeData[field]?.[idx]?.value ?? '';
        };

        const pickCompanyField = (arr = [], selIndex) => {
            if (!Array.isArray(arr) || arr.length === 0) return '';
            const idx = typeof selIndex === 'number' ? selIndex : 0;
            return arr[idx] ?? '';
        };

        const payload = {
            shared: formData.shared,
            shared_organizations: formData.shared_organizations || [],
            shared_users: formData.shared_users || [],
            associated_user_id: formData.associated_user_id,

            first_name: pickSingle('first_name'),
            middle_name: pickSingle('middle_name'),
            last_name: pickSingle('last_name'),
            email: pickSingle('email'),
            phone: pickSingle('phone'),

            details: {
                political_affiliation: pickSingle('political_affiliation'),
                reputation: pickSingle('reputation')
            },

            country_id: (() => {
                const idx = formData.selected?.country_id ?? 0;
                const keys = Object.keys(data.response.duplicate_data.country_id || {});
                return keys[idx] || '';
            })(),

            news_enabled: formData.news_enabled,
            news_frequency: formData.news_frequency,
            news_keywords: formData.news_keywords || [],

            companies: Object.entries(formData.companies || {}).map(([orgId, fields]) => ({
                organization_id: orgId,
                work_email: pickCompanyField(fields.work_email, fields.selected_work_email),
                work_phone: pickCompanyField(fields.work_phone, fields.selected_work_phone),
                job_title: pickCompanyField(fields.job_title, fields.selected_job_title)
            })),

            social_links: (formData.mergeMulti?.social_links || [])
                .filter((opt) => opt.keep)
                .map((opt) => ({
                    type: opt.type || '',
                    url: opt.value
                })),

            notes: (formData.mergeMulti?.notes || []).filter((opt) => opt.keep).map((opt) => ({ note: opt.value })),

            needs: (formData.needs || []).map((n) => ({ need_id: n.need_id })),
            skills: (formData.skills || []).map((s) => ({ skill_id: s.skill_id }))
        };

        try {
            const result = await updateContactDuplicatesItem({
                id: duplicateId,
                contactIds,
                data: payload
            }).unwrap();

            const newId = result.response?.contact_id;

            if (!newId) {
                console.error('No contact_id in merge response', result);
                return;
            }

            navigate(`/contacts/view/${newId}`);
        } catch (e) {
            console.error('Failed to merge duplicates', e);
        }
    };

    if (error) return <div>Error loading duplicate detail</div>;

    return (
        <AuthLayout title='Contact Duplicates Detail' backRoute='/contacts/duplicates'>
            {isLoading || !formReady ? (
                <ThreeDotsLoader className='mt-3' />
            ) : (
                <form onSubmit={handleSubmit(onSubmit)} className='max-w-3xl w-full mx-auto px-8 mt-6 mb-6'>
                    <Accordion className={`custom-accordion mt-2 pb-2`} multiple={false} activeIndex={null}>
                        <AccordionTab
                            key='shared-within'
                            header={<span className='text-lg font-bold text-theme1-text2'>Shared within</span>}
                        >
                            {renderShared()}
                        </AccordionTab>
                    </Accordion>

                    {singleFields.map((field) => {
                        if (!mergeDataValues[field] || mergeDataValues[field].length === 0) return null;
                        return (
                            <div key={field} className='mb-6'>
                                <div className='text-lg font-bold capitalize mb-2'>{field.replace('_', ' ')}</div>
                                {mergeDataValues[field].map((option, index) => (
                                    <div key={index} className='flex items-center gap-4 mb-2'>
                                        <Controller
                                            name={`selected.${field}`}
                                            control={control}
                                            render={({ field: radioField }) => (
                                                <BaseRadio
                                                    name={`selected.${field}`}
                                                    value={index}
                                                    control={control}
                                                    textValue=''
                                                    onChange={(e) => radioField.onChange(e.value)}
                                                />
                                            )}
                                        />
                                        {field === 'political_affiliation' ? (
                                            <Controller
                                                name={`mergeData.political_affiliation.${index}.value`}
                                                control={control}
                                                render={({ field: dropdownField }) => (
                                                    <BaseDropdown
                                                        control={control}
                                                        name={dropdownField.name}
                                                        options={politicalOptions}
                                                        placeholder='Select political affiliation'
                                                        defaultValue={option.value}
                                                        onChangeValue={(val) => dropdownField.onChange(val)}
                                                        clearErrors={clearErrors}
                                                        errors={[]}
                                                    />
                                                )}
                                            />
                                        ) : field === 'reputation' ? (
                                            <Controller
                                                name={`mergeData.reputation.${index}.value`}
                                                control={control}
                                                render={({ field: ratingField }) => (
                                                    <BaseRating
                                                        control={control}
                                                        name={ratingField.name}
                                                        value={ratingField.value}
                                                        setValue={(val) => {
                                                            ratingField.onChange(val);
                                                            clearErrors('mergeData.reputation');
                                                        }}
                                                        label=''
                                                        errors={[]}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <BaseInput
                                                name={`mergeData.${field}.${index}.value`}
                                                control={control}
                                                type='text'
                                                placeholder={`Enter ${field.replace('_', ' ')}`}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    {objectFields.map((field) => {
                        if (!mergeDataValues[field] || mergeDataValues[field].length === 0) return null;
                        return (
                            <div key={field} className='mb-6'>
                                <div className='text-lg font-bold capitalize mb-2'>
                                    {field === 'country_id' ? 'Country' : field.replace('_', ' ')}
                                </div>
                                {mergeDataValues[field].map((option, index) => (
                                    <div key={index} className='flex items-center gap-4 mb-2'>
                                        <Controller
                                            name={`selected.${field}`}
                                            control={control}
                                            render={({ field: radioField }) => (
                                                <BaseRadio
                                                    name={`selected.${field}`}
                                                    value={index}
                                                    control={control}
                                                    textValue=''
                                                    onChange={(e) => radioField.onChange(e.value)}
                                                />
                                            )}
                                        />
                                        <BaseInput
                                            name={`mergeData.${field}.${index}.value`}
                                            control={control}
                                            type='text'
                                            placeholder={`Enter Country`}
                                            readOnly
                                            className='cursor-not-allowed'
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    {multiFields.map((field) => {
                        if (!mergeMultiValues[field] || mergeMultiValues[field].length === 0) return null;
                        return (
                            <div key={field} className='mb-6'>
                                <div className='text-lg font-bold capitalize mb-2'>{field.replace('_', ' ')}</div>
                                {mergeMultiValues[field].map((option, index) => (
                                    <div key={index} className='flex items-center gap-4 mb-2'>
                                        <BaseCheckboxThemeGeneral
                                            name={`mergeMulti.${field}.${index}.keep`}
                                            control={control}
                                        />
                                        <BaseInput
                                            name={`mergeMulti.${field}.${index}.value`}
                                            control={control}
                                            type='text'
                                            placeholder={`Enter ${field}`}
                                        />
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    <div className='mb-10 border border-theme1-text3 p-4 rounded-2xl'>
                        <div className='text-theme1-primary font-bold text-xl mb-4'>News Settings</div>

                        <BaseSwitch
                            control={control}
                            name='news_enabled'
                            label={newsEnabled ? 'Disable news' : 'Enable news'}
                            clearErrors={clearErrors}
                            errors={[]}
                        />

                        {newsEnabled && (
                            <>
                                <div className='mt-4'>
                                    <div className='text-lg font-bold text-theme1-primary mb-1'>Frequency</div>
                                    <div className='flex items-center space-x-4'>
                                        <BaseRadio
                                            control={control}
                                            name='news_frequency'
                                            value='daily'
                                            textValue='Daily'
                                            clearErrors={clearErrors}
                                            errors={[]}
                                        />
                                        <BaseRadio
                                            control={control}
                                            name='news_frequency'
                                            value='weekly'
                                            textValue='Weekly'
                                            clearErrors={clearErrors}
                                            errors={[]}
                                        />
                                    </div>
                                </div>

                                <div className='mt-4'>
                                    <div className='text-lg font-bold text-theme1-primary mb-1'>News Keywords</div>
                                    <div className='flex items-center space-x-2'>
                                        <BaseInput
                                            type='text'
                                            placeholder='Enter keyword'
                                            value={newsKeyword}
                                            setValue={setNewsKeyword}
                                            errors={[]}
                                            className='w-3/4'
                                        />
                                        <BaseButton
                                            onClick={handleAddNewsKeyword}
                                            textValue='Add'
                                            className='btn-primary w-1/4 px-2 py-3'
                                        />
                                    </div>
                                    <div className='flex items-center flex-wrap mt-4'>
                                        {newsKeywords.map((keyword, i) => (
                                            <span
                                                key={i}
                                                className='mr-4 mb-4 px-4 py-2 border-2 border-theme1-gold1 bg-theme1-gold1 rounded-4xl text-white font-bold flex items-center'
                                            >
                                                <span>{keyword}</span>
                                                <span
                                                    onClick={() =>
                                                        setValue(
                                                            'news_keywords',
                                                            newsKeywords.filter((_, idx) => idx !== i)
                                                        )
                                                    }
                                                    className='ml-2 cursor-pointer'
                                                >
                                                    <IconClose className='w-5 h-5 fill-theme1-white' />
                                                </span>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {data?.response?.duplicate_data?.companies && (
                        <ContactCompaniesCarouselDuplicates
                            companiesData={data.response.duplicate_data.companies}
                            control={control}
                        />
                    )}

                    <NeedsAndSkillsDuplicates control={control} />

                    {noteFields.length > 0 && (
                        <div className='mb-6'>
                            <div className='text-lg font-bold capitalize mb-2'>Notes</div>
                            {noteFields.map((field, idx) => (
                                <div key={field.id} className='flex items-center gap-4 mb-2'>
                                    <BaseCheckboxThemeGeneral name={`mergeMulti.notes.${idx}.keep`} control={control} />

                                    <BaseInput
                                        name={`mergeMulti.notes.${idx}.value`}
                                        control={control}
                                        type='text'
                                        placeholder='Enter note'
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    <BaseButton
                        type='submit'
                        className={`bg-theme1-primary text-theme1-white flex-shrink-0 ${isUpdating ? 'bg-theme1-text3' : ''}`}
                        textValue='Merge Contacts'
                        disabled={isUpdating}
                    />
                </form>
            )}
        </AuthLayout>
    );
};

export default ContactDuplicatesDetail;
